import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { useSuccess } from '../contexts/SuccessContext';
import { loginWithPasskey, registerPasskey, isWebAuthnSupported } from '../lib/firebase/auth';

const AdminLogin: React.FC = () => {
  const [uid, setUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [supportsWebAuthn, setSupportsWebAuthn] = useState(false);
  const navigate = useNavigate();
  const { } = useAuth();
  const { addError } = useError();
  const { addSuccess } = useSuccess();

  useEffect(() => {
    setSupportsWebAuthn(isWebAuthnSupported());
  }, []);

  const handlePasskeyLogin = async () => {
    if (!supportsWebAuthn) {
      addError('Dein Browser unterstützt keine Passkeys. Bitte verwende einen modernen Browser.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await loginWithPasskey(uid || undefined);
      if (result.success) {
        localStorage.setItem('adminAuthenticated', 'true');
        addSuccess('Erfolgreich mit Passkey angemeldet!');
        navigate('/admin');
      } else {
        addError(result.error || 'Passkey-Anmeldung fehlgeschlagen');
      }
    } catch (error: any) {
      addError(error.message || 'Login fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterPasskey = async () => {
    if (!supportsWebAuthn) {
      addError('Dein Browser unterstützt keine Passkeys. Bitte verwende einen modernen Browser.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await registerPasskey();
      if (result.success && result.uid) {
        localStorage.setItem('adminAuthenticated', 'true');
        addSuccess(`Passkey erfolgreich registriert! Deine Admin ID: ${result.uid}`);
        navigate('/admin');
      } else {
        addError(result.error || 'Passkey-Registrierung fehlgeschlagen');
      }
    } catch (error: any) {
      addError(error.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  // Fallback für Demo-Zwecke
  const handleDemoLogin = () => {
    localStorage.setItem('adminAuthenticated', 'true');
    addSuccess('Demo-Admin-Zugang aktiviert');
    navigate('/admin');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handlePasskeyLogin();
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 p-4">
      <div className="text-center p-8 bg-gray-800 rounded-lg max-w-md mx-auto w-full">
        <h1 className="text-3xl font-bold text-yellow-400 font-display mb-4">Admin-Bereich</h1>
        
        {supportsWebAuthn ? (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Mit Passkey anmelden</h2>
              <p className="text-gray-400 mb-4 text-sm">
                Verwende deinen biometrischen Sensor oder Sicherheitsschlüssel
              </p>
              <input 
                type="text" 
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Admin ID (optional)"
                className="input mb-4 w-full bg-gray-700 text-white"
                disabled={isLoading}
              />
              <button 
                onClick={handlePasskeyLogin} 
                className="btn btn-primary w-full mb-2"
                disabled={isLoading}
              >
                {isLoading ? 'Anmelden...' : '🔐 Mit Passkey anmelden'}
              </button>
            </div>
            
            <div className="border-t border-gray-700 pt-6 mb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Neu hier?</h2>
              <p className="text-gray-400 mb-4 text-sm">
                Registriere einen neuen Admin-Passkey
              </p>
              <button 
                onClick={handleRegisterPasskey} 
                className="btn btn-secondary w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Registrieren...' : '🆕 Neuen Passkey registrieren'}
              </button>
            </div>
          </>
        ) : (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">WebAuthn nicht unterstützt</h2>
            <p className="text-gray-400 mb-4 text-sm">
              Dein Browser unterstützt keine Passkeys. Bitte verwende Chrome, Safari, Edge oder Firefox in einer aktuellen Version.
            </p>
          </div>
        )}
        
        <div className="border-t border-gray-700 pt-6">
          <button 
            onClick={() => navigate('/')} 
            className="btn btn-secondary w-full mb-2"
          >
            Zurück zur Startseite
          </button>
          
          {/* Demo-Zugang für Entwicklung */}
          {import.meta.env.DEV && (
            <button 
              onClick={handleDemoLogin} 
              className="text-gray-500 text-sm hover:text-gray-400 mt-2"
            >
              Demo-Admin-Zugang (nur Development)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 